import styled from 'styled-components';

import { theme, breakpoints, Col, IconRegularMinus, IconRegularPlus } from '@notino/react-styleguide-v20';

import { Loader } from '@sharedComponents/Loader';
import { defaultFontSizeCSS, StyledBasicRowWrapper } from '@styles/generalStyles';
import { IBasicTestProps } from '@styles/model';
import { CypressTestNamespace } from '@utils/test';

import { DropDownQuantitySelector } from '../../Inputs';
import { CartPrice, CheckoutPrice } from '../../Prices';

interface IGeneralProductItemProps extends IBasicTestProps {
  disabled?: boolean;
  isEngraving?: boolean;
  isSummaryLayout?: boolean;
}

export const UndoButtonStyled = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: white;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
`;

export const IconWrapper = styled.button<{ isVisible?: boolean; isMinus: boolean; enabled: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border-style: hidden;
  background-color: #f7f7f7;
  display: flex;
  justify-content: center;
  align-items: center;
  visibility: ${({ isVisible = true }) => (isVisible ? 'visible' : 'hidden')};
  opacity: ${({ isVisible = true }) => (isVisible ? 1 : 0)};
  --offset: 0;
  &:hover {
    cursor: pointer;
  }
`;

export const StyledIconRegularMinus = styled(IconRegularMinus)`
  width: 0.75rem;
  height: 0.75rem;
`;

export const StyledIconRegularPlus = styled(IconRegularPlus)`
  width: 1rem;
  height: 1rem;
`;

export const QuantityValue = styled.span<{ enabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 1rem;
  color: ${theme.color.text.primary};
  font-size: 0.875rem;
  font-weight: ${({ enabled = true }) => (enabled ? 'bold' : 'normal')};
  margin: 0 1rem;
`;

export const StyledLoader = styled(Loader)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  margin: 0 1rem;
  height: 0.75rem;
  padding-top: 0.25rem;
  color: ${theme.color.text.primary};
`;

export const QuantityUnit = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.25rem;
  height: 100%;
  color: ${theme.color.text.primary};
  font-size: 0.875rem;
  font-weight: normal;
`;

export const QuantityUnitHidden = styled.span`
  position: absolute;
  width: 1rem;
  height: 1rem;
  padding: 0rem;
  margin: -1rem;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const StyledGeneralProductRow = styled(StyledBasicRowWrapper)`
  display: flex;
  margin: 0;

  @media (min-width: ${breakpoints.sm}) {
    align-items: center;
  }

  @media (min-width: ${breakpoints.md}) {
    margin: 1rem;
  }
`;

export const StyledDropDownQuantitySelector = styled(DropDownQuantitySelector)`
  width: 6.25rem;
  ${defaultFontSizeCSS};

  @media (min-width: ${breakpoints.sm}) {
    width: 6.25rem;
    padding: 0;
  }
`;

export const StyledPriceTotal = styled(CartPrice)<IGeneralProductItemProps>`
  color: ${({ disabled }) => (disabled ? theme.color.text.disabled : theme.color.text.primary)};
  font-size: '0.875rem';
`;

export const StyledImageCol = styled(Col)`
  order: 1;
  padding-left: 0.625rem;
  margin: auto;

  @media (min-width: ${breakpoints.sm}) {
    padding-left: 0;
  }
`;

export const StyledProductCol = styled(Col).attrs(() => ({
  className: `${CypressTestNamespace.ECartPageProducts.PRODUCT_NAME}`,
}))<IGeneralProductItemProps>`
  justify-content: flex-start;
  order: ${({ isEngraving }) => (isEngraving ? 0 : 1)};
  padding-left: ${({ isEngraving }) => (isEngraving ? 1.25 : 2.5)}rem;
  padding-right: 0;

  @media (min-width: ${breakpoints.sm}) {
    margin-bottom: 0;
    padding-left: 2rem;
    order: 1;
  }

  @media (min-width: ${breakpoints.md}) {
    padding: 0 1rem;
  }
`;

export const StyledPricePerUnitCol = styled(Col)<IGeneralProductItemProps>`
  order: 3;

  @media (min-width: ${breakpoints.sm}) {
    visibility: visible;
    display: flex;
    flex-flow: column;
    text-align: right;
    padding-right: 0;
  }

  @media (min-width: ${breakpoints.md}) {
    justify-content: flex-end;
  }

  @media (max-width: ${breakpoints.sm}) {
    order: 6;
    align-self: center;
  }
`;

export const StyledPricePerUnitContent = styled.div<IGeneralProductItemProps>`
  color: ${({ disabled }) => (disabled ? theme.color.text.disabled : theme.color.text.primary)};

  > div {
    justify-content: flex-end;
    margin-right: -0.937rem;
    margin-left: -0.9375rem;
  }
`;

export const StyledQuantityCol = styled(Col).attrs(() => ({
  className: `${CypressTestNamespace.ECartPageProducts.PRODUCT_QUANTITY}`,
}))`
  order: 5;
  display: flex;
  padding: 1rem 0 1rem 2.5rem;
  justify-content: flex-start;
  align-items: center;

  @media (min-width: ${breakpoints.sm}) {
    padding: 0;
    justify-content: center;
  }
`;

export const StyledTotalPriceCol = styled(Col).attrs(() => ({
  className: `${CypressTestNamespace.ECartPageProducts.PRODUCT_PRICE}`,
}))<IGeneralProductItemProps>`
  order: 7;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-weight: bold;
  padding-right: 0;
  padding-left: 0.5rem;

  @media (min-width: ${breakpoints.sm}) {
    order: 5;
    padding: 0 0.5rem;
  }

  @media (max-width: ${breakpoints.sm}) {
    display: ${({ isSummaryLayout }) => (isSummaryLayout ? 'flex' : 'none')};
  }
`;

export const TextButtonCol = styled(Col).attrs(() => ({
  className: `${CypressTestNamespace.ECartPageProducts.PRODUCT_REMOVE_BUTTON}`,
}))`
  order: 6;
  align-items: center;
  padding-left: 0;

  @media (min-width: ${breakpoints.sm}) {
    text-align: right;
    justify-content: flex-end;
  }

  @media (min-width: ${breakpoints.md}) {
    padding-left: 0.9375rem;
  }
  @media (max-width: ${breakpoints.sm}) {
    order: 2;
    text-align: right;
    padding-right: 0;
  }
`;

export const StyledLink = styled.a`
  text-decoration: underline;
  padding-left: 0.25rem;

  &:hover {
    text-decoration: none;
  }
`;

export const StyledOldPriceWrapper = styled.div<{ dualPriceLayout: boolean }>`
  display: flex;
  flex-flow: row;
  justify-content: ${({ dualPriceLayout }) => (dualPriceLayout ? 'center' : 'flex-end')};
`;

export const StyledOldTotalPriceWrapper = styled(StyledBasicRowWrapper)`
  display: none;

  @media (max-width: ${breakpoints.sm}) {
    display: flex;
  }
`;

export const StyledPricePerUnitWrapper = styled.div`
  @media (max-width: ${breakpoints.sm}) {
    display: none;
  }
`;

export const StyledPricePerUnitPlaceholderCol = styled(Col)<IGeneralProductItemProps>`
  order: 3;
  display: none;

  @media (max-width: ${breakpoints.sm}) {
    visibility: hidden;
    display: block;
  }
`;

export const StyledTooltipLink = styled(StyledLink)`
  display: block;
`;

export const SCheckoutPrice = styled(CheckoutPrice)<{ dualPriceLayout: boolean }>`
  justify-content: ${({ dualPriceLayout }) => (dualPriceLayout ? 'center' : 'flex-start')};
  order: ${({ dualPriceLayout }) => (dualPriceLayout ? 2 : 0)};
`;
